import { useCallback, useEffect, useMemo } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { theme } from '@sg/garnish';
import type { Address } from '@sg/graphql-schema';

import { getEnvVars } from '@order/utils';

import { formatRadarAddress, useAddressAutocomplete } from '../../../../../hooks';
import { AddressSearchField, AddressSearchResultList, SEARCH_RESULT_HEIGHT } from './components';

import type { MinimumAddress } from '../../../../../locations.types';
import type { AddressForm } from '../../address.config';

export const AddressFormPredictions = (props: RadarAddressFormPredictionsProps) => {
  const {
    savedCustomerAddress,
    selectedAddress,
    visible,
    disabled,
    accessibilityLabel,
    setValue,
    setSelectedAddress,
  } = props;

  // ─── Auto Complete State ───────────────────────────────────

  const {
    loading,
    error,
    noResults,
    searchString,
    predictions,
    setSearchString,
    clearAutoCompleteResults,
  } = useAddressAutocomplete({
    apiKey: getEnvVars().RADAR_SDK_KEY,
  });

  // ─── Memoized Results ──────────────────────────────────────

  const predictionsData = useMemo(() => {
    return { predictions, loading, error, noResults };
  }, [error, loading, noResults, predictions]);

  // ─── Effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!visible || !savedCustomerAddress) return;
    const formattedAddress = formatRadarAddress(savedCustomerAddress);
    const address = mapCustomerAddressToMinimumAddress(savedCustomerAddress);

    setSearchString(formattedAddress ?? '');
    setSelectedAddress(address);
  }, [savedCustomerAddress, visible, setSearchString, setSelectedAddress]);

  useEffect(() => {
    if (!selectedAddress) {
      return;
    }

    const { formattedAddress, latitude, longitude, street, zipCode, city, state, country } =
      selectedAddress;

    clearAutoCompleteResults();
    setSearchString(formattedAddress ?? '');
    setValue('latitude', latitude, { shouldDirty: true });
    setValue('longitude', longitude, { shouldDirty: true });
    setValue('street', street, { shouldDirty: true });
    setValue('zipCode', zipCode, { shouldDirty: true });
    setValue('city', city, { shouldDirty: true });
    setValue('state', state, { shouldDirty: true });
    setValue('country', country, { shouldDirty: true });
  }, [setValue, clearAutoCompleteResults, setSearchString, selectedAddress]);

  // ─── Callbacks ─────────────────────────────────────────────

  const handleChangeText = useCallback(
    (input: string) => {
      setSelectedAddress(undefined);
      setSearchString(input);
    },
    [setSearchString, setSelectedAddress],
  );

  // ─── Result Checks ─────────────────────────────────────────

  const hasSelectedAddress = Boolean(selectedAddress);
  const hasEnteredMinChars = searchString.length >= 3;
  const isLoadingOrReady = loading || !noResults;
  const shouldShowPredictions = hasEnteredMinChars && !hasSelectedAddress && isLoadingOrReady;

  return (
    <>
      <AddressSearchField
        isRequired
        disabled={disabled}
        value={searchString}
        hasResults={!noResults}
        accessibilityLabel={accessibilityLabel}
        onChangeText={handleChangeText}
        onSubmit={setSearchString}
      />

      {shouldShowPredictions ? (
        <View testID="location.auto-complete-results.wrapper" style={styles.autoCompleteWrapper}>
          <AddressSearchResultList
            style={styles.autoCompleteResultContent}
            predictionsData={predictionsData}
            onPress={setSelectedAddress}
          />
        </View>
      ) : null}
    </>
  );
};

// ─── Address Map Helper ───────────────────────────────────────────────────────────────────

const mapCustomerAddressToMinimumAddress = (savedCustomerAddress: Partial<Address>) => {
  const formattedAddress = formatRadarAddress(savedCustomerAddress);

  return {
    formattedAddress,
    latitude: savedCustomerAddress.latitude ?? 0,
    longitude: savedCustomerAddress.longitude ?? 0,
    street: savedCustomerAddress.street ?? '',
    zipCode: savedCustomerAddress.zipCode ?? '',
    city: savedCustomerAddress.city ?? '',
    state: savedCustomerAddress.state ?? '',
    country: savedCustomerAddress.country ?? '',
  } satisfies MinimumAddress;
};

// ─── Types ───────────────────────────────────────────────────

type RadarAddressFormPredictionsProps = Readonly<{
  savedCustomerAddress: Partial<Address> | undefined;
  selectedAddress: MinimumAddress | undefined;
  visible?: boolean;
  disabled?: boolean;
  accessibilityLabel?: string;
  setValue: UseFormSetValue<AddressForm>;
  setSelectedAddress: (address: MinimumAddress | undefined) => void;
}>;

// ─── Styles ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  autoCompleteWrapper: {
    position: 'absolute',
    top: theme.spacing['14'],
    left: 0,
    right: 0,
    minHeight: SEARCH_RESULT_HEIGHT * 3,
    backgroundColor: theme.colors.APP_BACKGROUND,
    borderBottomColor: theme.colors.NEUTRAL_4,
    borderBottomWidth: 1,
    zIndex: theme.zIndex.popover,
  },
  autoCompleteResultContent: {
    paddingHorizontal: theme.spacing['10'],
  },
});
